/**
 * Toolbox Talk wizard config — data-driven step definitions.
 *
 * Phase 20 (Refactor 3, supplemented in Phase 20-02). Mirrors
 * `TOOLBOX_TALK_WIZARD_STEPS` (../wizard-schemas/toolbox-talk.ts).
 * 6 content steps + preview.
 *
 * Step renderer assignment:
 *   - `topic`     → custom (`ToolboxTalkTopicPicker` — library/custom
 *                   mode switch + cross-field `refine` validation)
 *   - `body`      → data-driven (textarea + AI suggest)
 *   - `keyPoints` → custom (`ToolboxTalkKeyPointsBuilder` — dynamic
 *                   array of strings with add/remove)
 *   - `questions` → custom (`ToolboxTalkQuestionsBuilder`)
 *   - `attendees` → custom (`ToolboxTalkAttendeesPicker` — worker /
 *                   subcontractor selector)
 *   - `delivery`  → custom (`ToolboxTalkDeliveryBuilder` — combines
 *                   date + delivered-by + a chip-row duration picker
 *                   that the generic primitives don't draw today)
 *   - `preview`   → custom (`ToolboxTalkPreview`)
 *
 * AI section-fill is wired on the talk body — Phase 15c added the
 * dispatcher route.
 */
import type { WizardConfig } from "./types";

export const TOOLBOX_TALK_WIZARD_CONFIG: WizardConfig = {
  documentType: "TOOLBOX_TALK",
  steps: [
    {
      id: "topic",
      titleKey: "wizards.toolboxTalk.stepTitles.topic",
      descriptionKey: "wizards.toolboxTalk.stepDescriptions.topic",
      schemaRef: "TOOLBOX_TALK.steps.topic",
      customRenderer: "ToolboxTalkTopicPicker",
    },
    {
      id: "body",
      titleKey: "wizards.toolboxTalk.stepTitles.body",
      descriptionKey: "wizards.toolboxTalk.stepDescriptions.body",
      schemaRef: "TOOLBOX_TALK.steps.body",
      fields: [
        {
          id: "talkBody",
          type: "textarea",
          labelKey: "wizards.toolboxTalk.fields.talkBody",
          placeholderKey: "wizards.toolboxTalk.placeholders.talkBody",
          required: true,
          rows: 6,
        },
      ],
      aiSectionSuggest: {
        enabled: true,
        documentType: "TOOLBOX_TALK",
        sectionKey: "talkBody",
        targetFieldId: "talkBody",
      },
    },
    {
      id: "keyPoints",
      titleKey: "wizards.toolboxTalk.stepTitles.keyPoints",
      descriptionKey: "wizards.toolboxTalk.stepDescriptions.keyPoints",
      schemaRef: "TOOLBOX_TALK.steps.keyPoints",
      customRenderer: "ToolboxTalkKeyPointsBuilder",
    },
    {
      id: "questions",
      titleKey: "wizards.toolboxTalk.stepTitles.questions",
      descriptionKey: "wizards.toolboxTalk.stepDescriptions.questions",
      schemaRef: "TOOLBOX_TALK.steps.questions",
      customRenderer: "ToolboxTalkQuestionsBuilder",
    },
    {
      id: "attendees",
      titleKey: "wizards.toolboxTalk.stepTitles.attendees",
      descriptionKey: "wizards.toolboxTalk.stepDescriptions.attendees",
      schemaRef: "TOOLBOX_TALK.steps.attendees",
      customRenderer: "ToolboxTalkAttendeesPicker",
    },
    {
      id: "delivery",
      titleKey: "wizards.toolboxTalk.stepTitles.delivery",
      descriptionKey: "wizards.toolboxTalk.stepDescriptions.delivery",
      schemaRef: "TOOLBOX_TALK.steps.delivery",
      customRenderer: "ToolboxTalkDeliveryBuilder",
    },
    {
      id: "preview",
      titleKey: "wizards.toolboxTalk.stepTitles.preview",
      descriptionKey: "wizards.toolboxTalk.stepDescriptions.preview",
      schemaRef: "TOOLBOX_TALK.steps.preview",
      customRenderer: "ToolboxTalkPreview",
    },
  ],
};
